import type {
  DataProvider,
  BaseRecord,
  GetListParams,
  GetOneParams,
  UpdateParams,
  CreateParams,
  CrudFilter,
} from '@refinedev/core'
import simpleRestProvider from '@refinedev/simple-rest'
import axios from 'axios'

import { API_CONFIG } from './constants.ts'

import { auth } from '@/app/config/firebase'
import { appointmentsApi } from '@/features/osa-scheduling/api/appointments.api'
import type { Appointment } from '@/features/osa-scheduling/types'
import type { OsaEnvironment } from '@/shared/types'

const axiosInstance = axios.create()
axiosInstance.interceptors.request.use(async config => {
  let token: string | null = null
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_TOKEN) {
    token = import.meta.env.VITE_DEV_AUTH_TOKEN as string
  } else {
    const user = auth.currentUser
    if (user) {
      token = await user.getIdToken()
    }
  }
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
})

const baseDataProvider = simpleRestProvider(API_CONFIG.baseUrl, axiosInstance)

export const archonDataProvider: DataProvider = {
  ...baseDataProvider,
  getList: async <TData extends BaseRecord = BaseRecord>(params: GetListParams) => {
    if (params.resource === 'appointments' || params.resource === 'onsite-appointment') {
      // Extract filters
      let status: string | undefined
      let query: string | undefined
      let env: OsaEnvironment | undefined
      let type: string | undefined
      let subtype: string[] | undefined
      let funnel: string | undefined
      if (params.filters && params.filters.length > 0) {
        params.filters.forEach((filter: CrudFilter) => {
          if ('field' in filter && filter.field === 'status' && 'value' in filter && filter.value) {
            status = filter.value as string
          }
          if ('field' in filter && filter.field === 'q' && 'value' in filter && filter.value) {
            query = filter.value as string
          }
          if ('field' in filter && filter.field === 'env' && 'value' in filter && filter.value) {
            env = filter.value as OsaEnvironment
          }
          if ('field' in filter && filter.field === 'type' && 'value' in filter && filter.value) {
            type = filter.value as string
          }
          if (
            'field' in filter &&
            filter.field === 'subtype' &&
            'value' in filter &&
            filter.value
          ) {
            subtype = filter.value as string[]
          }
          if ('field' in filter && filter.field === 'funnel' && 'value' in filter && filter.value) {
            funnel = filter.value as string
          }
        })
      }

      // Extract pagination
      const { current = 1, pageSize = 10 } = params.pagination || {}

      // Extract sorting
      let sortField: string | undefined
      let sortOrder: string | undefined
      if (params.sorters && params.sorters.length > 0) {
        const sorter = params.sorters[0]
        if ('field' in sorter) {
          sortField = sorter.field
          sortOrder = sorter.order
        }
      }

      const result = await appointmentsApi.getAppointments({
        page: current,
        pageSize,
        status,
        query,
        sortField,
        sortOrder,
        env,
        type,
        subtype,
        funnel,
      })

      return {
        data: result.data as unknown as TData[],
        total: result.total,
      }
    }
    return baseDataProvider.getList<TData>(params)
  },
  getOne: async <TData extends BaseRecord = BaseRecord>(params: GetOneParams) => {
    if (params.resource === 'appointments' || params.resource === 'onsite-appointment') {
      const result = await appointmentsApi.getAppointmentById(params.id as string)
      return { data: result.data as unknown as TData }
    }
    return baseDataProvider.getOne<TData>(params)
  },
  create: async <TData extends BaseRecord = BaseRecord, TVariables = Record<string, unknown>>(
    params: CreateParams<TVariables>
  ) => {
    return baseDataProvider.create<TData, TVariables>(params)
  },
  update: async <TData extends BaseRecord = BaseRecord, TVariables = Record<string, unknown>>(
    params: UpdateParams<TVariables>
  ) => {
    if (params.resource === 'appointments' || params.resource === 'onsite-appointment') {
      const result = await appointmentsApi.updateAppointment(
        params.id as string,
        params.variables as Partial<Appointment>
      )
      return { data: result.data as unknown as TData }
    }
    return baseDataProvider.update<TData, TVariables>(params)
  },
}

export const dataProvider = archonDataProvider
